import type { Product, ProductAccessory } from "interfaces/schemas/nodeSchema";
import { createNodesInput } from "stores/project/project/actions/utils/createNodeInput";
import { NodeKinds } from "interfaces/enums";
import { isProduct } from "interfaces/utils/identifyNodeTypes";
import type { ProductInput, ProductOutput } from "../product.types";

export const createProductNode = (
  input: ProductInput,
  output: ProductOutput,
  quantity: number,
) => {
  const pimId = output.productImageList?.productImages?.find(
    (image) => image?.typ === "ProductPhoto",
  )?.pimId;
  const product: Product = {
    id: "dummy-product-node-id",
    parentId: "dummy-parent-id",
    position: 0,
    partiallyVisible: false,
    active: true,
    quantity: quantity,
    kind: NodeKinds.PRODUCT,
    productName: output.product?.orderCode?.key ?? "",
    orderCode: {
      state: {
        optionStates: input.state?.orderCodeState?.optionStates,
      },
      seriesId: input.seriesId,
      currentStrategyId: input.state?.currentStrategyId,
      propertyStates: input.state?.propertyStates ?? [],
    },
    presentation: output.product?.orderCode?.presentation ?? "",
    pimId: pimId ?? null,
    productDescription: null,
    price: null,
    priceGroup: null,
    selectedAccessories: null,
    nodes:
      output.product?.selectedAccessories?.map((accessory) => {
        if (!accessory?.seriesId) {
          throw new Error("seriesId is missing for accessory");
        }
        if (!accessory?.currentStrategyId) {
          throw new Error("currentStrategyId is missing for accessory");
        }
        if (!accessory?.currentPresentation) {
          throw new Error("currentPresentation is missing for accessory");
        }
        if (!accessory?.accessoryId) {
          throw new Error("accessoryId is missing for accessory");
        }
        if (!accessory?.accessoryGroupId) {
          throw new Error("accessoryGroupId is missing for accessory");
        }
        const acc: ProductAccessory = {
          id: "accessory-node-id",
          parentId: "dummy-product-node-id",
          position: 0,
          partiallyVisible: false,
          accessoryId: accessory.accessoryId,
          accessoryGroupId: accessory.accessoryGroupId,
          active: true,
          quantity: quantity,
          kind: NodeKinds.PRODUCT_ACCESSORY,
          productName: getAccessoryNameFromGroup(
            output,
            accessory?.currentPresentation,
            accessory?.accessoryGroupId,
          ),
          orderCode: {
            state: {
              optionStates: accessory?.optionStates,
            },
            seriesId: accessory?.seriesId,
            currentStrategyId: accessory?.currentStrategyId,
          },
          presentation: accessory?.currentPresentation,
          pimId: accessory?.pimId ?? null,
          productDescription: null,
          nodes: [],
          price: null,
          priceGroup: null,
          selectedAccessories: null,
        };
        return acc;
      }) ?? [],
  };
  const productNode = createNodesInput({
    data: product,
  });
  if (productNode.error) {
    throw new Error(productNode.error.message);
  }
  if (!productNode.subTree) {
    throw new Error("Subtree is not available after creating product node");
  }
  if (!isProduct(productNode.subTree)) {
    throw new Error("The created node is not a product node");
  }
  return productNode.subTree as Product;
};

export const getAccessoryNameFromGroup = (
  productOutput: ProductOutput,
  presentation?: string | null,
  accessoryGroupId?: string | null,
) => {
  if (!presentation || !accessoryGroupId) {
    throw new Error("Accessory presentation and Group ID must be provided");
  }
  const accessoryGroups = productOutput.product?.accessoryGroups;
  if (!accessoryGroups) {
    throw new Error("Accessory groups are not available in the product output");
  }
  const foundGroup = accessoryGroups.find(
    (group) => group?.id === accessoryGroupId,
  );
  if (!foundGroup) {
    throw new Error(`Accessory group with ID ${accessoryGroupId} not found`);
  }
  const foundAccessory = foundGroup.accessories?.find(
    (acc) => acc?.currentPresentation === presentation,
  );
  if (!foundAccessory) {
    throw new Error(
      `Accessory with presentation ${presentation} not found in group ${accessoryGroupId}`,
    );
  }
  if (!foundAccessory.serieAlias) {
    throw new Error(
      `Serie alias is not available for accessory with presentation ${presentation} in group ${accessoryGroupId}`,
    );
  }
  return foundAccessory.serieAlias;
};
